import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AppContainer from "../../Components/Accounting/AppContainer";
import DataTable from "../../Components/Accounting/DataTable";
import Select2 from "../../Components/Accounting/Select2";
import { RenderSecure } from "../../util/script/RenderSecure";
import ajaxAccounting from "../../util/accounting/remote/ajaxAccounting";
import functions from "../../util/accounting/functions";
import toast, { Toaster } from "react-hot-toast";

const ENTRY_TYPE_LABELS = {
  general: "General Journal",
  contra: "Contra Entry",
  expense: "Expense Entry",
  income: "Income Entry",
  depreciation: "Depreciation",
  appreciation: "Appreciation",
};

const JournalEntries = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [journals, setJournals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFY, setSelectedFY] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState(searchParams.get("type") || "");

  useEffect(() => { fetchFinancialYears(); }, []);
  useEffect(() => { if (selectedFY) fetchJournals(); }, [selectedFY, filterStatus, filterType]);

  const fetchFinancialYears = async () => {
    const res = await ajaxAccounting.listFinancialYears({});
    if (res.status === "OK") {
      setFinancialYears(res.details || []);
      const cur = (res.details || []).find((y) => y.is_current === 1);
      setSelectedFY(String(cur?.id || res.details?.[0]?.id || ""));
    }
  };

  const fetchJournals = async () => {
    setLoading(true);
    const res = await ajaxAccounting.listJournals({
      financial_year_id: parseInt(selectedFY),
      status: filterStatus || undefined,
      type: filterType || undefined,
    });
    if (res.status === "OK") setJournals(res.details?.list || (Array.isArray(res.details) ? res.details : []));
    else toast.error(res.message || "Failed to fetch journals");
    setLoading(false);
  };

  const postJournal = async (id) => {
    const res = await ajaxAccounting.postJournal({ id });
    if (res.status === "OK") { toast.success("Journal posted!"); fetchJournals(); }
    else toast.error(res.message);
  };

  const statusBadge = (status) => {
    const map = { draft: "warning", posted: "success", reversed: "secondary", void: "danger" };
    return <span className={`badge bg-${map[status] || "secondary"}`}>{status}</span>;
  };

  const columns = [
    { key: "reference", label: "Reference", render: (v, row) => <strong>{v || row.id}</strong> },
    { key: "entry_date", label: "Date", render: (v) => functions.formatDate(v) },
    { key: "type", label: "Type", render: (v) => ENTRY_TYPE_LABELS[v] || v },
    { key: "description", label: "Description", render: (v) => <span className="tx-12">{v}</span> },
    { key: "status", label: "Status", render: (v) => statusBadge(v) },
    {
      key: "id", label: "Actions",
      render: (id, row) => (
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-light" onClick={() => navigate(`/accounting/journal/${id}`)} title="View"><i className="fa-solid fa-eye"></i></button>
          {row.status === "draft" && (
            <RenderSecure code="ACC-JOURNAL-POST">
              <button className="btn btn-sm btn-success" onClick={() => postJournal(id)} title="Post"><i className="fa-solid fa-check"></i></button>
            </RenderSecure>
          )}
          {row.status === "posted" && (
            <RenderSecure code="ACC-JOURNAL-REVERSE">
              <button className="btn btn-sm btn-outline-warning" onClick={() => navigate(`/accounting/journal/${id}/reverse`)} title="Reverse"><i className="fa-solid fa-rotate-left"></i></button>
            </RenderSecure>
          )}
        </div>
      ),
    },
  ];

  const fyOptions = financialYears.map((y) => ({ value: String(y.id), label: y.name }));
  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "draft", label: "Draft" },
    { value: "posted", label: "Posted" },
    { value: "reversed", label: "Reversed" },
  ];
  const typeOptions = [
    { value: "", label: "All Types" },
    ...Object.entries(ENTRY_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l })),
  ];

  return (
    <AppContainer
      title="Journal Entries"
      previousPageTitle="Accounting"
      description="All financial journal entries are listed here. Draft entries need to be posted to affect the ledger. Use filters to narrow by type or status. Contra entries automatically restrict accounts to cash/bank pairs."
      actions={[
        { label: "General Journal", icon: "fa-solid fa-plus", className: "btn btn-primary", onClick: () => navigate("/accounting/journal/new?type=general") },
        { label: "Contra Entry", icon: "fa-solid fa-arrows-left-right", className: "btn btn-outline-secondary", onClick: () => navigate("/accounting/journal/new?type=contra") },
        { label: "Expense Entry", icon: "fa-solid fa-minus-circle", className: "btn btn-outline-danger", onClick: () => navigate("/accounting/journal/new?type=expense") },
        { label: "Income Entry", icon: "fa-solid fa-plus-circle", className: "btn btn-outline-success", onClick: () => navigate("/accounting/journal/new?type=income") },
      ]}
    >
      <Toaster />
      <div className="card custom-card p-3 mb-3">
        <div className="row g-2">
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Financial Year</label>
            <Select2 options={fyOptions} value={selectedFY} onChange={setSelectedFY} placeholder="Select Year" />
          </div>
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Status</label>
            <Select2 options={statusOptions} value={filterStatus} onChange={setFilterStatus} placeholder="All Status" />
          </div>
          <div className="col-lg-4">
            <label className="form-label tx-12 fw-semibold">Entry Type</label>
            <Select2 options={typeOptions} value={filterType} onChange={setFilterType} placeholder="All Types" />
          </div>
        </div>
      </div>

      <div className="card custom-card p-3">
        <DataTable columns={columns} data={journals} loading={loading} pageSize={15} emptyMessage="No journal entries found for the selected filters." />
      </div>
    </AppContainer>
  );
};

export default JournalEntries;
